import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { OWNER } from "@/data/portfolio";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "portfolio", label: "Portfolio" },
  { id: "skills", label: "Skills" },
  { id: "contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      // active section detection
      for (const l of LINKS) {
        const el = document.getElementById(l.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(l.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navigation"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <a href="#home" onClick={go("home")} className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-xl bg-[#FF3B30] text-white grid place-items-center font-display font-black text-lg shadow-lg shadow-red-500/20">S</span>
          <span className="font-display font-bold text-lg tracking-tight">
            {OWNER.firstName}<span className="text-[#FF3B30]">.</span>
          </span>
        </a>

        <ul className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                onClick={go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className={`relative text-sm font-medium transition-colors ${
                  active === l.id ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {l.label}
                {active === l.id && (
                  <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#FF3B30] rounded-full" />
                )}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:block">
          <a
            href="#contact"
            onClick={go("contact")}
            className="btn-primary text-sm !py-3 !px-5"
            data-testid="nav-hire-me-btn"
          >
            Hire Me
          </a>
        </div>

        <button
          className="md:hidden w-11 h-11 grid place-items-center rounded-xl border border-white/10 bg-white/5"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-white/10 backdrop-blur-xl bg-black/80" data-testid="nav-mobile-menu">
          <ul className="flex flex-col px-6 py-6 gap-4">
            {LINKS.map((l) => (
              <li key={l.id}>
                <a
                  href={`#${l.id}`}
                  onClick={go(l.id)}
                  className="block text-base font-medium text-white/80 hover:text-[#FF3B30] py-2"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a href="#contact" onClick={go("contact")} className="btn-primary w-full justify-center mt-2" data-testid="nav-mobile-hire-me-btn">
                Hire Me
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
